import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  MenuItem,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Phone as PhoneIcon,
  Warning as WarningIcon,
  CheckCircle as SafeIcon,
  Block as BlockIcon,
  TrendingUp as TrendingIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/api';
import { format } from 'date-fns';

// Tab Panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Stat card component
const StatCard = ({ icon, label, value, color = 'primary' }) => (
  <Card variant="outlined">
    <CardContent sx={{ textAlign: 'center', py: 2 }}>
      <Box sx={{ color: `${color}.main`, mb: 1 }}>{icon}</Box>
      <Typography variant="h4" fontWeight={600}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </CardContent>
  </Card>
);

const PhoneReputation = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // List state
  const [numbers, setNumbers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [filterKnownScam, setFilterKnownScam] = useState('');
  const [stats, setStats] = useState(null);
  
  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState(null);
  const [topReported, setTopReported] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  
  // Detail dialog
  const [detailDialog, setDetailDialog] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Edit dialog
  const [editDialog, setEditDialog] = useState(null);
  const [editData, setEditData] = useState({});
  
  // Import dialog
  const [importDialog, setImportDialog] = useState(false);
  const [importText, setImportText] = useState('');
  const [importLoading, setImportLoading] = useState(false);

  useEffect(() => {
    if (tabValue === 0) {
      loadDashboard();
    } else {
      loadNumbers();
    }
  }, [tabValue]);

  useEffect(() => {
    if (tabValue === 1) {
      loadNumbers();
    }
  }, [page, rowsPerPage, filterKnownScam]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getPhoneReputationStats();
      setDashboardStats(response.data.stats);
      setTopReported(response.data.topReported || []);
      setRecentReports(response.data.recentReports || []);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadNumbers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getPhoneReputationList({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        known_scam: filterKnownScam || undefined,
        search: search || undefined,
      });
      setNumbers(response.data.numbers || []);
      setTotal(response.data.total || 0);
      setStats(response.data.stats);
    } catch (err) {
      console.error('Failed to load numbers:', err);
      setError('Failed to load phone numbers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    loadNumbers();
  };

  const loadDetail = async (phoneNumber) => {
    try {
      setDetailLoading(true);
      setDetailDialog(phoneNumber);
      const response = await adminAPI.getPhoneReputation(phoneNumber);
      setDetailData(response.data);
    } catch (err) {
      console.error('Failed to load detail:', err);
      setError('Failed to load phone details');
      setDetailDialog(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleEdit = (number) => {
    setEditData({
      phone_number: number.phone_number,
      known_scam: number.known_scam || false,
      known_spam: number.known_spam || false,
      verified_safe: number.verified_safe || false,
      scam_type: number.scam_type || '',
      notes: number.notes || '',
    });
    setEditDialog(number.phone_number);
  };

  const handleSaveEdit = async () => {
    try {
      await adminAPI.updatePhoneReputation(editDialog, editData);
      setSuccess('Phone reputation updated');
      setEditDialog(null);
      loadNumbers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update');
    }
  };

  const handleDelete = async (phoneNumber) => {
    if (!window.confirm(`Delete reputation record for ${phoneNumber}?`)) return;
    try {
      await adminAPI.deletePhoneReputation(phoneNumber);
      setSuccess('Phone reputation deleted');
      loadNumbers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  const handleImport = async () => {
    try {
      setImportLoading(true);
      
      // Parse CSV/text input
      const lines = importText.trim().split('\n');
      const numbers = lines.map(line => {
        const parts = line.split(',').map(p => p.trim());
        return {
          phone_number: parts[0],
          known_scam: parts[1]?.toLowerCase() !== 'false',
          scam_type: parts[2] || null,
          notes: parts[3] || null,
        };
      }).filter(n => n.phone_number);

      const response = await adminAPI.importPhoneReputation(numbers);
      setSuccess(`Imported: ${response.data.imported} new, ${response.data.updated} updated, ${response.data.errors?.length || 0} errors`);
      setImportDialog(false);
      setImportText('');
      loadNumbers();
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
    } finally {
      setImportLoading(false);
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    // Simple US format
    if (phone.startsWith('+1') && phone.length === 12) {
      return `(${phone.slice(2, 5)}) ${phone.slice(5, 8)}-${phone.slice(8)}`;
    }
    return phone;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Phone Reputation
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track and manage reported phone numbers
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            startIcon={<UploadIcon />}
            onClick={() => setImportDialog(true)}
          >
            Import
          </Button>
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={tabValue === 0 ? loadDashboard : loadNumbers} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Dashboard" />
          <Tab label="All Numbers" />
        </Tabs>
      </Paper>

      {/* Dashboard Tab */}
      <TabPanel value={tabValue} index={0}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : dashboardStats && (
          <>
            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={4} md={2}>
                <StatCard 
                  icon={<PhoneIcon />} 
                  label="Total Numbers" 
                  value={dashboardStats.total_numbers || 0}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <StatCard 
                  icon={<BlockIcon />} 
                  label="Known Scams" 
                  value={dashboardStats.known_scam_count || 0}
                  color="error"
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <StatCard 
                  icon={<WarningIcon />} 
                  label="Known Spam" 
                  value={dashboardStats.known_spam_count || 0}
                  color="warning"
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <StatCard 
                  icon={<SafeIcon />} 
                  label="Verified Safe" 
                  value={dashboardStats.verified_safe_count || 0}
                  color="success"
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <StatCard 
                  icon={<TrendingIcon />} 
                  label="Total Reports" 
                  value={dashboardStats.total_reports || 0}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <StatCard 
                  icon={<TrendingIcon />} 
                  label="Avg Reports/Number" 
                  value={dashboardStats.avg_reports_per_number || '0'}
                />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              {/* Top Reported */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>Top Reported Numbers</Typography>
                  {topReported.length === 0 ? (
                    <Typography color="text.secondary">No reported numbers yet</Typography>
                  ) : (
                    <List dense>
                      {topReported.map((num) => (
                        <ListItem 
                          key={num.phone_number}
                          secondaryAction={
                            <IconButton size="small" onClick={() => loadDetail(num.phone_number)}>
                              <ViewIcon />
                            </IconButton>
                          }
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {formatPhone(num.phone_number)}
                                {num.known_scam && <Chip label="Scam" size="small" color="error" />}
                              </Box>
                            }
                            secondary={`${num.report_count} reports • ${num.scam_type || 'Unknown type'}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>
              </Grid>

              {/* Recent Reports */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>Recent Reports</Typography>
                  {recentReports.length === 0 ? (
                    <Typography color="text.secondary">No recent reports</Typography>
                  ) : (
                    <List dense>
                      {recentReports.map((report) => (
                        <ListItem key={report.id}>
                          <ListItemText
                            primary={formatPhone(report.phone_number)}
                            secondary={
                              <>
                                <Chip label={report.report_type} size="small" sx={{ mr: 1 }} />
                                {report.reporter_name || 'Anonymous'} • {format(new Date(report.created_at), 'MMM d, HH:mm')}
                              </>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </TabPanel>

      {/* All Numbers Tab */}
      <TabPanel value={tabValue} index={1}>
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Search phone number"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  endAdornment: (
                    <IconButton size="small" onClick={handleSearch}>
                      <SearchIcon />
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={filterKnownScam}
                onChange={(e) => { setFilterKnownScam(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Known Scam</MenuItem>
                <MenuItem value="false">Not Confirmed</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                {total} numbers total
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Numbers Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Phone Number</TableCell>
                  <TableCell>Reports</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Type</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Last Reported</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : numbers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No phone numbers found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  numbers.map((num) => (
                    <TableRow key={num.phone_number} hover>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {formatPhone(num.phone_number)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{num.report_count} total</Typography>
                          {num.scam_report_count > 0 && (
                            <Typography variant="caption" color="error.main">
                              {num.scam_report_count} scam
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {num.known_scam && <Chip label="Scam" size="small" color="error" sx={{ mr: 0.5 }} />}
                        {num.known_spam && <Chip label="Spam" size="small" color="warning" sx={{ mr: 0.5 }} />}
                        {num.verified_safe && <Chip label="Safe" size="small" color="success" />}
                        {!num.known_scam && !num.known_spam && !num.verified_safe && '-'}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{num.scam_type || '-'}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {num.last_reported_at ? format(new Date(num.last_reported_at), 'MMM d, yyyy') : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View details">
                          <IconButton size="small" onClick={() => loadDetail(num.phone_number)}>
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(num)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(num.phone_number)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      </TabPanel>

      {/* Detail Dialog */}
      <Dialog open={!!detailDialog} onClose={() => setDetailDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon />
            Phone Details: {formatPhone(detailDialog)}
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : detailData && (
            <Grid container spacing={3}>
              {/* Reputation Info */}
              {detailData.reputation && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Reputation Summary</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="text.secondary">Total Reports</Typography>
                        <Typography variant="h6">{detailData.reputation.report_count}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="text.secondary">Scam Reports</Typography>
                        <Typography variant="h6" color="error.main">{detailData.reputation.scam_report_count}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="text.secondary">Status</Typography>
                        <Box>
                          {detailData.reputation.known_scam && <Chip label="Known Scam" size="small" color="error" />}
                          {detailData.reputation.verified_safe && <Chip label="Verified Safe" size="small" color="success" />}
                          {!detailData.reputation.known_scam && !detailData.reputation.verified_safe && <Chip label="Unconfirmed" size="small" />}
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="text.secondary">Type</Typography>
                        <Typography>{detailData.reputation.scam_type || '-'}</Typography>
                      </Grid>
                    </Grid>
                    {detailData.reputation.notes && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">Notes</Typography>
                        <Typography>{detailData.reputation.notes}</Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              )}

              {/* Report History */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Report History ({detailData.reports?.length || 0})</Typography>
                  {detailData.reports?.length === 0 ? (
                    <Typography color="text.secondary">No reports</Typography>
                  ) : (
                    <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {detailData.reports?.map((report) => (
                        <ListItem key={report.id}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip label={report.report_type} size="small" />
                                {report.scam_category && <Typography variant="body2">{report.scam_category}</Typography>}
                              </Box>
                            }
                            secondary={`${report.reporter_name || report.reporter_email || 'System'} • ${format(new Date(report.created_at), 'MMM d, yyyy HH:mm')}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>
              </Grid>

              {/* Recent Events */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Recent Events ({detailData.recentEvents?.length || 0})</Typography>
                  {detailData.recentEvents?.length === 0 ? (
                    <Typography color="text.secondary">No events</Typography>
                  ) : (
                    <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {detailData.recentEvents?.map((event) => (
                        <ListItem key={event.id}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip label={event.channel} size="small" />
                                {event.concern_level && (
                                  <Chip 
                                    label={event.concern_level} 
                                    size="small" 
                                    color={event.concern_level.includes('high') ? 'error' : 'default'}
                                  />
                                )}
                              </Box>
                            }
                            secondary={`${event.user_name || event.user_email || 'Unknown'} • ${format(new Date(event.created_at), 'MMM d, yyyy HH:mm')}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(null)}>Close</Button>
          {detailData?.reputation && (
            <Button variant="outlined" onClick={() => { setDetailDialog(null); handleEdit(detailData.reputation); }}>
              Edit
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Phone Reputation</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Phone Number"
            value={editData.phone_number || ''}
            disabled
            margin="normal"
          />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editData.known_scam || false}
                    onChange={(e) => setEditData({ ...editData, known_scam: e.target.checked, verified_safe: e.target.checked ? false : editData.verified_safe })}
                    color="error"
                  />
                }
                label="Known Scam"
              />
            </Grid>
            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editData.known_spam || false}
                    onChange={(e) => setEditData({ ...editData, known_spam: e.target.checked })}
                    color="warning"
                  />
                }
                label="Known Spam"
              />
            </Grid>
            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editData.verified_safe || false}
                    onChange={(e) => setEditData({ ...editData, verified_safe: e.target.checked, known_scam: e.target.checked ? false : editData.known_scam })}
                    color="success"
                  />
                }
                label="Verified Safe"
              />
            </Grid>
          </Grid>
          <TextField
            select
            fullWidth
            label="Scam Type"
            value={editData.scam_type || ''}
            onChange={(e) => setEditData({ ...editData, scam_type: e.target.value })}
            margin="normal"
          >
            <MenuItem value="">None</MenuItem>
            <MenuItem value="irs">IRS / Tax Scam</MenuItem>
            <MenuItem value="tech_support">Tech Support</MenuItem>
            <MenuItem value="grandparent">Grandparent Scam</MenuItem>
            <MenuItem value="sweepstakes">Sweepstakes / Lottery</MenuItem>
            <MenuItem value="debt_collection">Fake Debt Collection</MenuItem>
            <MenuItem value="government">Government Impersonation</MenuItem>
            <MenuItem value="utility">Utility Scam</MenuItem>
            <MenuItem value="romance">Romance Scam</MenuItem>
            <MenuItem value="investment">Investment Scam</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Notes"
            value={editData.notes || ''}
            onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialog} onClose={() => setImportDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import Phone Numbers</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter phone numbers, one per line. Format: phone_number, known_scam (true/false), scam_type, notes
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Example:<br />
            +18005551234, true, irs, Known IRS scam<br />
            +18005555678, true, tech_support, Microsoft support scam<br />
            +12025551234
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={10}
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            placeholder="+18005551234, true, irs, Notes here"
            sx={{ fontFamily: 'monospace' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleImport} 
            disabled={importLoading || !importText.trim()}
          >
            {importLoading ? <CircularProgress size={20} /> : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PhoneReputation;
